import { useState } from 'react';
import { message } from 'antd';
import { ReplaceMeterRecordDb } from 'indexed_db/replaceMeterDb';
import { useReplaceMeterStore } from 'routes/meter_replacement/useStore';
import { useServices } from 'services/useServices';
import { captureException } from '@sentry/react';
import { useIntl } from 'react-intl';
import { useScrollTopOnMount } from './useScrollTopOnMount';
import { customApiKeyAuthenticator } from '../services/httpClient';
import { SynchronizeStatus, SynchronizeStep } from '../consts/sync';
import { SynchronizeReplaceMeters as messages } from './messages';

export interface SynchronizeReplaceMetersShape {
  syncStatus: SynchronizeStatus;
  syncOfflineRecords: () => void;
}

export const useSynchronizeReplaceMeters = (): SynchronizeReplaceMetersShape => {
  const forceScrollTop = useScrollTopOnMount();
  const { queuedMetersCount, setQueuedMetersCount, setIsSendingData, reset } = useReplaceMeterStore();
  const intl = useIntl();

  const [syncStatus, setSyncStatus] = useState<SynchronizeStatus>({
    syncStep: SynchronizeStep.Confirm,
    queuedCount: queuedMetersCount ?? 0,
    syncedCount: 0,
    rejectedCount: 0,
  });

  const {
    api: { inventoryMetersApiClient },
    indexedDb: { replaceMeterDb },
  } = useServices();

  const synchronizeReplaceMeters = async (items: ReplaceMeterRecordDb[]) => {
    // we want to send meters sequentially
    for (const { id, replacementMeter, locationApiKey } of items) {
      try {
        await inventoryMetersApiClient.replace(replacementMeter, {
          authenticator: customApiKeyAuthenticator(locationApiKey),
        });
        await replaceMeterDb.setSyncedToRecord(id);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          syncedCount: prevState.syncedCount + 1,
        }));
      } catch (err) {
        message.error(intl.formatMessage(messages.errorMessage, { meterId: replacementMeter.inventoryMeterId }));
        console.error(err);
        captureException(err);
        setSyncStatus(prevState => ({
          ...prevState,
          syncStep: SynchronizeStep.InProgress,
          rejectedCount: prevState.rejectedCount + 1,
        }));
      }
    }
  };

  const syncOfflineRecords = async () => {
    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.GetReady }));
    setIsSendingData(true);

    const replaceMetersData = await replaceMeterDb.getData();
    await synchronizeReplaceMeters(replaceMetersData.filter(item => !item.wasSynced));

    setSyncStatus(prevState => ({ ...prevState, syncStep: SynchronizeStep.Finished }));

    // update queued meters count
    const metersCount = await replaceMeterDb.countNotSynchronized();

    reset();
    setQueuedMetersCount(metersCount);
    setIsSendingData(false);

    forceScrollTop();
  };

  return { syncStatus, syncOfflineRecords };
};
